import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import { StandaloneFlightMenu } from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidget'
import DropdownSheet from 'components/Luxkit/Dropdown/Sheet/DropdownSheet'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import Group from 'components/utils/Group'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { FlightsFareTypes } from 'constants/flight'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import useFlightDeal from 'hooks/Flights/useFlightDeal'
import { addDays } from 'lib/datetime/dateUtils'
import React, { ComponentProps, useCallback, useContext, useEffect } from 'react'
import FormattedFlightSearchDate from './FlightSearchMenu/FormattedFlightSearchDate'
import FlightsDatePicker from './FlightsDatePicker/FlightsDatePicker'

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'anchorRef'> {
  className?: string;
}

const earliestBookingDate = addDays(new Date(), 1)
const finalBookingDate = addDays(new Date(), 330)

function FlightSearchDateSelect({ className, anchorRef }: Props) {
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const {
    checkinDate,
    checkoutDate,
    activeMenu,
    fareType,
    flightDealId,
  } = state

  const [flightDeal] = useFlightDeal(flightDealId)

  const setActiveMenu = useCallback((activeMenu?: StandaloneFlightMenu) => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu })
  }, [dispatch])

  const cancelMenu = useCallback(() => {
    setActiveMenu(undefined)
  }, [setActiveMenu])

  const openMenu = useCallback(() => {
    setActiveMenu(StandaloneFlightMenu.date)
  }, [setActiveMenu])

  useEffect(() => {
    if (fareType.value === FlightsFareTypes.ONE_WAY) {
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, date: null })
    }
  }, [dispatch, fareType.value])

  return (
    <div className={className}>
      <SearchFormFieldGroup>
        <SearchFormField
          label="Departing"
          placeholder="Select date"
          required
          requiredErrorMessage="Please select travel dates"
          value={checkinDate?.format(DMY_CASUAL_FORMAT)}
          onClick={openMenu}
        />
        {fareType.value === FlightsFareTypes.RETURN && <SearchFormField
          label="Returning"
          placeholder="Select date"
          required
          requiredErrorMessage="Please select travel dates"
          value={checkoutDate?.format(DMY_CASUAL_FORMAT)}
          onClick={openMenu}
        />}
      </SearchFormFieldGroup>
      <DropdownSheet
        size="fill-anchor"
        open={activeMenu === StandaloneFlightMenu.date}
        anchorRef={anchorRef}
        onClose={cancelMenu}
        primaryActionProps={{
          'data-testid': 'apply-flight-search-changes',
          type: 'submit',
          children: 'Apply',
          onClick: cancelMenu,
        }}
        secondaryActionProps={{
          kind: 'tertiary',
          children: 'Cancel',
          onClick: cancelMenu,
        }}
        footerStart={<Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <BodyText variant="small" colour="neutral-two">Based on recent return prices (per adult) found</BodyText>
          {!!(checkinDate && checkoutDate) && <FormattedFlightSearchDate checkInDate={checkinDate} checkOutDate={checkoutDate} />}
        </Group>}
      >
        <VerticalSpacer gap={24}>
          {flightDeal && <FlightDealDuration flightDeal={flightDeal} />}
          <FlightsDatePicker
            minDate={earliestBookingDate}
            maxDate={finalBookingDate}
            type="dual"
          />
        </VerticalSpacer>
      </DropdownSheet>
    </div>
  )
}

export default FlightSearchDateSelect
